import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  AlertTriangle, Clock, Crown, X, Sparkles, 
  CheckCircle2, ArrowRight, Lock
} from "lucide-react";

// Trial Expiration Modal - Shows when trial is about to expire
export const TrialExpirationModal = ({ 
  isOpen, 
  onClose, 
  hoursRemaining, 
  daysRemaining,
  isExpired,
  restaurantName,
  savingsEstimate,
  remainingSpots
}) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (!isOpen || daysRemaining > 1) return;

    const totalSeconds = hoursRemaining * 3600;
    
    const updateCountdown = () => {
      const now = new Date();
      const remaining = Math.max(0, totalSeconds - Math.floor((now.getTime() % (totalSeconds * 1000)) / 1000));
      
      setCountdown({
        hours: Math.floor(remaining / 3600),
        minutes: Math.floor((remaining % 3600) / 60),
        seconds: remaining % 60
      });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, [isOpen, hoursRemaining, daysRemaining]);

  const handleBecomeFounder = () => {
    onClose();
    navigate("/trial/checkout");
  };

  if (!isOpen) return null;

  // Different states: Expiring Soon (< 24h), Expired (grace period), Locked
  const isExpiringSoon = !isExpired && daysRemaining <= 1;
  const isInGracePeriod = isExpired && hoursRemaining > 0;
  const isLocked = isExpired && hoursRemaining <= 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-lg bg-gradient-to-br from-[#0A1244] to-[#060D3A] rounded-2xl border border-gold/40 shadow-2xl overflow-hidden"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors z-10"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>

          {/* Urgency Header */}
          <div className={`px-6 py-5 text-center ${
            isLocked ? "bg-slate-800/50" :
            isExpired ? "bg-red-500/20" : 
            "bg-gradient-to-r from-orange-500/20 to-red-500/20"
          }`}>
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
              isLocked ? "bg-slate-700 border-2 border-slate-600" :
              isExpired ? "bg-red-500/20 border-2 border-red-500" :
              "bg-orange-500/20 border-2 border-orange-500 animate-pulse"
            }`}>
              {isLocked ? (
                <Lock className="w-8 h-8 text-slate-400" />
              ) : isExpired ? (
                <AlertTriangle className="w-8 h-8 text-red-400" />
              ) : (
                <Clock className="w-8 h-8 text-orange-400" />
              )}
            </div>

            <h2 className={`text-xl font-inter font-black ${
              isLocked ? "text-slate-400" :
              isExpired ? "text-red-400" : 
              "text-orange-400"
            }`}>
              {isLocked ? "Trial Locked" :
               isExpired ? "Trial Expired" :
               "Trial Ending Soon!"}
            </h2>

            {!isLocked && (
              <p className="text-slate-400 text-sm mt-2">
                {isExpired 
                  ? `24-hour grace period ending soon`
                  : `${restaurantName}, your trial ends in:`}
              </p>
            )}

            {/* Countdown Timer */}
            {(isExpiringSoon || isInGracePeriod) && (
              <div className="flex justify-center gap-4 mt-4">
                {[
                  { value: countdown.hours, label: "Hours" },
                  { value: countdown.minutes, label: "Min" },
                  { value: countdown.seconds, label: "Sec" }
                ].map((item, i) => (
                  <div key={i} className="text-center">
                    <div className={`text-3xl font-inter font-black ${
                      isExpired ? "text-red-400" : "text-orange-400"
                    }`}>
                      {String(item.value).padStart(2, '0')}
                    </div>
                    <div className="text-xs text-slate-500">{item.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            {isLocked ? (
              <div className="text-center">
                <p className="text-slate-400 mb-4">
                  Your trial data has been archived. Start a new trial or contact us to restore your account.
                </p>
                <Button
                  onClick={() => navigate("/")}
                  className="bg-slate-700 hover:bg-slate-600 text-white"
                >
                  Return Home
                </Button>
              </div>
            ) : (
              <>
                {/* What you'll lose */}
                <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4 mb-4">
                  <p className="text-sm font-medium text-red-400 mb-2">
                    {isExpired ? "You're about to lose:" : "When your trial ends, you'll lose:"}
                  </p>
                  <ul className="text-sm text-slate-400 space-y-1">
                    <li>❌ Access to all AI Supervisors</li>
                    <li>❌ Your SOPs and recipes</li>
                    <li>❌ Vision analysis history</li>
                    <li>❌ AI receptionist service</li>
                  </ul>
                </div>

                {/* Founder offer */}
                <div className="bg-gold/10 border border-gold/30 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Crown className="w-5 h-5 text-gold" />
                    <span className="font-inter font-bold text-gold">Founding Member Offer</span>
                  </div>
                  
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-2xl font-inter font-black text-gold">$374</span>
                      <span className="text-slate-400">/month</span>
                    </div>
                    <div className="text-right">
                      <span className="text-slate-500 line-through">$499/month</span>
                      <span className="block text-xs text-lime">Save 25% for life</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Sparkles className="w-3 h-3 text-gold" />
                    <span>Only <strong className="text-gold">{remainingSpots}</strong> spots remaining</span>
                  </div>
                </div>

                {/* Benefits */}
                <div className="space-y-2 mb-6">
                  {[
                    "All your trial data transfers automatically",
                    "24/7 AI receptionist on your phone number",
                    "Unlimited SOP creation & vision analysis",
                    "Priority support from the Argus team"
                  ].map((benefit, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-lime" />
                      <span className="text-slate-300">{benefit}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <Button
                  onClick={handleBecomeFounder}
                  className={`w-full font-bold py-4 ${
                    isExpired 
                      ? "bg-red-500 hover:bg-red-600 text-white animate-pulse"
                      : "bg-gold hover:bg-gold/90 text-indigo-700"
                  }`}
                  data-testid="modal-become-founder-btn"
                >
                  <Crown className="w-5 h-5 mr-2" />
                  {isExpired ? "Claim My Spot Now" : "Become a Founding Member"}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>

                <p className="text-center text-xs text-slate-500 mt-3">
                  First month free • Cancel anytime
                </p>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TrialExpirationModal;
